import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  AppMembershipEntity,
  AppUserEntity,
  COMPANY_FLAG_FEATURES,
  COMPANY_FLAG_ROLES,
  CompanyEntity,
  COUNTRY_FEATURES,
  CustomReportFileMetaEntity,
  CustomReportTemplateFileMetaEntity,
  FileMetaEntity,
  PartnerReportEntity,
  PartnerReportOrderEntity,
  PartnerReportTypeKey,
  PERSON_FLAG_FEATURES,
  PERSON_FLAG_ROLES,
} from '@nk/data-entities-lib';
import { Observable } from 'rxjs';

export interface CustomReportFileMetaCompositeEntity {
  uuid: string;
  file_meta: FileMetaEntity;
  app_membership: AppMembershipEntity;
  app_user: AppUserEntity;
  custom_report_file_meta: CustomReportFileMetaEntity;
  company: CompanyEntity;
  app_url: string;
}

export interface PartnerReportComposite {
  partner_report: Partial<PartnerReportEntity>;
  file_meta: FileMetaEntity;
  partner_report_order: PartnerReportOrderEntity;
}

export interface CreditSafeReport {
  company_uuid: string;
  partner_report_type_key:
    | PartnerReportTypeKey.creditsafe_pdf_company_report
    | PartnerReportTypeKey.creditsafe_json_company_report;
}

export interface PartnerReportOrderComposite {
  partner_report_order: PartnerReportOrderEntity;
  partner_report_composites: Partial<PartnerReportComposite>[];
}

export interface CustomReportTemplateFileMetaCompositeEntity {
  uuid: string;
  file_meta: FileMetaEntity;
  app_membership: AppMembershipEntity;
  app_user: AppUserEntity;
  custom_report_template_file_meta: CustomReportTemplateFileMetaEntity;
  app_url: string;
}

@Injectable()
export class CustomReportService {
  constructor(
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
  ) {}

  public postCustomReportFileMeta(payload: CustomReportFileMetaEntity): Observable<CustomReportFileMetaEntity> {
    // TODO: test that this works
    return this._analysisApiAccess.post<CustomReportFileMetaEntity>(
      `custom-report-file-meta`,
      payload,
      {},
      [
        AppFeatureKey.base,
        ...COMPANY_FLAG_FEATURES,
        ...PERSON_FLAG_FEATURES,
        AppFeatureKey.custom_company_report,
        ...COUNTRY_FEATURES,
      ],
      [...COMPANY_FLAG_ROLES, ...PERSON_FLAG_ROLES],
    );
  }

  public deleteCustomReportFileMeta(customReportFileMetaEntityUuid: string): Observable<CustomReportFileMetaEntity> {
    return this._analysisApiAccess.delete<CustomReportFileMetaEntity>(
      `custom-report-file-meta/${customReportFileMetaEntityUuid}`,
      {},
      [AppFeatureKey.base, AppFeatureKey.custom_company_report],
    );
  }

  public customReportFileMetaCompositeListByCustomerAndCompany = this._cachingService.create(
    this,
    this.fetchCustomReportFileMetaCompositeListByCustomerAndCompany,
  );
  public fetchCustomReportFileMetaCompositeListByCustomerAndCompany(
    customerUuid: string,
    companyUuid: string,
  ): Observable<CustomReportFileMetaCompositeEntity[]> {
    return this._analysisApiAccess.get<CustomReportFileMetaCompositeEntity[]>(
      `app-customer/${customerUuid}/company/${companyUuid}/custom-report-file-meta-composite`,
      {},
      [
        AppFeatureKey.base,
        AppFeatureKey.custom_company_report,
        ...COMPANY_FLAG_FEATURES,
        ...PERSON_FLAG_FEATURES,
        ...COUNTRY_FEATURES,
      ],
      [...COMPANY_FLAG_ROLES, ...PERSON_FLAG_ROLES],
    );
  }

  public customReportTemplateFileMetaCompositeListByCustomerCache = this._cachingService.create(
    this,
    this.fetchCustomReportTemplateFileMetaCompositeListByCustomer,
  );
  public fetchCustomReportTemplateFileMetaCompositeListByCustomer(
    customerUuid: string,
  ): Observable<CustomReportTemplateFileMetaCompositeEntity[]> {
    return this._analysisApiAccess.get<CustomReportTemplateFileMetaCompositeEntity[]>(
      `app-customer/${customerUuid}/custom-report-template-file-meta-composite`,
      {},
      [AppFeatureKey.base, AppFeatureKey.custom_company_report],
    );
  }

  public postCustomReportTemplateFileMetaComposite(
    appCustomerUuid: string,
    payload: Partial<CustomReportTemplateFileMetaCompositeEntity>,
  ): Observable<CustomReportTemplateFileMetaCompositeEntity> {
    return this._analysisApiAccess.post<CustomReportTemplateFileMetaCompositeEntity>(
      `app-customer/${appCustomerUuid}/custom-report-template-file-meta-composite`,
      payload,
      {},
      [AppFeatureKey.base, AppFeatureKey.custom_company_report],
    );
  }
}
